import axios, { type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from "axios";
import * as React from "react";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { record, type eventWithTime } from "rrweb";

type AxiosConfigWithMetadata = InternalAxiosRequestConfig & {
	metadata?: {
		axiosId: string;
	};
};

const getAuthToken = () => localStorage.getItem("access_token");
const getRefreshToken = () => localStorage.getItem("refresh_token");

const toExpiryTimestamp = (expiresInSeconds?: unknown): string | null => {
	const seconds = Number(expiresInSeconds);
	if (!Number.isFinite(seconds) || seconds <= 0) {
		return null;
	}
	return String(Date.now() + Math.floor(seconds * 1000));
};

const TOKEN_ENDPOINT_PATH = "/v1/auth/v1/token";
const TRACEPARENT_HEADER = "traceparent";
const REPORT_ERROR_ENDPOINT_PATH = "/v1/timekeeper/v1/report_error";
const DOM_REPLAY_WINDOW_MS = 3 * 60 * 1000;
const DOM_REPLAY_MAX_EVENTS = 4000;
const DOM_REPLAY_MAX_PAYLOAD_CHARS = 1_800_000;
const RRWEB_EVENT_FULL_SNAPSHOT = 2;
const RRWEB_EVENT_META = 4;

type DomReplaySnapshot = {
	format: "rrweb@2";
	session_id: string;
	started_at: string;
	captured_at: string;
	window_ms: number;
	event_count: number;
	truncated: boolean;
	events: eventWithTime[];
};

type DomReplayBufferState = {
	sessionId: string;
	startedAt: string;
	events: eventWithTime[];
	stopRecording: ((...args: any[]) => void) | null;
};

let domReplayState: DomReplayBufferState | null = null;

const clearSessionStorage = () => {
	localStorage.removeItem("access_token");
	localStorage.removeItem("token_type");
	localStorage.removeItem("expires_in");
	localStorage.removeItem("expires_at");
	localStorage.removeItem("refresh_token");
	localStorage.removeItem("refresh_expires_in");
	localStorage.removeItem("refresh_expires_at");
	localStorage.removeItem("permissions");
	sessionStorage.removeItem("authenticated");
};

const redirectToSignin = () => {
	if (typeof window !== "undefined" && !window.location.pathname.startsWith("/signin")) {
		window.location.assign("/signin");
	}
};

let isRefreshing = false;
let pendingQueue: Array<(token: string | null) => void> = [];

const subscribeTokenRefresh = (cb: (token: string | null) => void) => {
	pendingQueue.push(cb);
};

const onRefreshed = (token: string | null) => {
	pendingQueue.forEach((cb) => cb(token));
	pendingQueue = [];
};

const refreshAccessToken = async (): Promise<string | null> => {
	const refreshToken = getRefreshToken();
	if (!refreshToken) {
		return null;
	}

	const clientId = `${process.env.REACT_APP_CLIENT_ID || ""}`.replace(/"/g, "").trim();
	if (!clientId) {
		return null;
	}

	const endpoint = `${process.env.REACT_APP_TK_GW_DOMAIN}${TOKEN_ENDPOINT_PATH}`;
	const body = new URLSearchParams();
	body.append("grant_type", "refresh_token");
	body.append("client_id", clientId);
	body.append("refresh_token", refreshToken);

	try {
		const response = await axios.post(endpoint, body, {
			headers: { "Content-Type": "application/x-www-form-urlencoded" },
			timeout: 1000 * 30,
			// @ts-expect-error: marca interna para que ningún interceptor intente refrescar este request
			_skipAuthRefresh: true,
		});
		const data = response.data;
		if (!data?.access_token) {
			return null;
		}
		localStorage.setItem("access_token", data.access_token);
		if (data.token_type) localStorage.setItem("token_type", data.token_type);
		if (data.expires_in) {
			localStorage.setItem("expires_in", String(data.expires_in));
			const expiresAt = toExpiryTimestamp(data.expires_in);
			if (expiresAt) localStorage.setItem("expires_at", expiresAt);
		}
		if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
		if (data.refresh_expires_in) {
			localStorage.setItem("refresh_expires_in", String(data.refresh_expires_in));
			const refreshExpiresAt = toExpiryTimestamp(data.refresh_expires_in);
			if (refreshExpiresAt) localStorage.setItem("refresh_expires_at", refreshExpiresAt);
		}
		return data.access_token as string;
	} catch (err) {
		return null;
	}
};

const randomHex = (bytes: number): string => {
	const buffer = new Uint8Array(bytes);

	if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
		crypto.getRandomValues(buffer);
	} else {
		for (let i = 0; i < buffer.length; i += 1) {
			buffer[i] = Math.floor(Math.random() * 256);
		}
	}

	return Array.from(buffer, (byte) => byte.toString(16).padStart(2, "0")).join("");
};

const generateTraceparent = (): string => {
	let traceId = randomHex(16);
	while (/^0+$/.test(traceId)) {
		traceId = randomHex(16);
	}

	let parentId = randomHex(8);
	while (/^0+$/.test(parentId)) {
		parentId = randomHex(8);
	}

	const version = "00";
	const traceFlags = "01";

	return `${version}-${traceId}-${parentId}-${traceFlags}`;
};

const interceptRequest = (config: AxiosConfigWithMetadata) => {
	const token = getAuthToken();
	if (token) {
		config.headers.Authorization = `Bearer ${token}`;
	}
	config.headers.traceparent = generateTraceparent();
	if (typeof window !== "undefined") {
		config.headers["X-Client-Route"] = getCurrentRoute();
		config.headers["X-Client-Url"] = getCurrentUrl();
	}
	return config;
};

const interceptSuccessResponse = (response: AxiosResponse) => {
	return response;
};

const readHeaderValue = (headers: any, headerName: string): string | null => {
	if (!headers) return null;

	const normalizedHeaderName = headerName.toLowerCase();

	if (typeof headers.get === "function") {
		const value = headers.get(headerName) ?? headers.get(normalizedHeaderName);
		return typeof value === "string" && value.trim() !== "" ? value : null;
	}

	const value = headers[headerName] ?? headers[normalizedHeaderName];
	return typeof value === "string" && value.trim() !== "" ? value : null;
};

const extractRequestTraceparent = (error: any): string | null => {
	const traceparentFromRequest = readHeaderValue(error?.config?.headers, TRACEPARENT_HEADER);
	if (traceparentFromRequest) {
		return traceparentFromRequest;
	}

	return null;
};

const extractTraceIdFromTraceparent = (traceparent: string): string | null => {
	const traceparentParts = traceparent.split("-");
	if (traceparentParts.length !== 4) {
		return null;
	}

	const traceId = traceparentParts[1];
	if (!/^[a-f0-9]{32}$/i.test(traceId)) {
		return null;
	}

	return traceId;
};

const buildReportErrorEndpoint = (): string | null => {
	const gatewayBaseUrl = `${process.env.REACT_APP_TK_GW_DOMAIN || ""}`.replace(/\/+$/, "");
	if (!gatewayBaseUrl) {
		return null;
	}

	return `${gatewayBaseUrl}${REPORT_ERROR_ENDPOINT_PATH}`;
};

const parseErrorData = (data: any): any => {
	if (typeof data !== "string") {
		return data;
	}

	try {
		return JSON.parse(data);
	} catch {
		return data;
	}
};

const createDomReplaySessionId = (): string => {
	return `${Date.now()}-${randomHex(8)}`;
};

const pruneDomReplayEvents = (events: eventWithTime[]) => {
	if (events.length > DOM_REPLAY_MAX_EVENTS) {
		events.splice(0, events.length - DOM_REPLAY_MAX_EVENTS);
	}
};

const startDomReplayCaptureIfNeeded = (): void => {
	if (typeof window === "undefined" || typeof document === "undefined") {
		return;
	}

	if (domReplayState) {
		return;
	}

	const events: eventWithTime[] = [];
	const stopRecording = record({
		emit(event) {
			events.push(event);
			pruneDomReplayEvents(events);
		},
		checkoutEveryNms: 60 * 1000,
		maskAllInputs: false,
		recordCrossOriginIframes: false,
		sampling: {
			mousemove: 120,
			scroll: 150,
			media: 800,
			input: "last",
		},
	});

	domReplayState = {
		sessionId: createDomReplaySessionId(),
		startedAt: new Date().toISOString(),
		events,
		stopRecording: stopRecording ?? null,
	};
};

const getDomReplaySnapshot = (): DomReplaySnapshot | null => {
	if (!domReplayState) {
		return null;
	}

	const allEvents = domReplayState.events;
	const cutoff = Date.now() - DOM_REPLAY_WINDOW_MS;
	let events = allEvents.filter((event) => Number(event?.timestamp ?? 0) >= cutoff);
	let truncated = false;

	const hasFullSnapshot = events.some((event) => Number(event?.type) === RRWEB_EVENT_FULL_SNAPSHOT);
	if (!hasFullSnapshot) {
		const firstEventTs = Number(events[0]?.timestamp ?? Date.now());
		const priorEvents = allEvents.filter((event) => Number(event?.timestamp ?? 0) < firstEventTs);
		const fullSnapshotIndex = priorEvents.findLastIndex(
			(event) => Number(event?.type) === RRWEB_EVENT_FULL_SNAPSHOT
		);

		if (fullSnapshotIndex >= 0) {
			const fullSnapshotTs = Number(priorEvents[fullSnapshotIndex]?.timestamp ?? 0);
			const latestMeta = priorEvents.findLast(
				(event) =>
					Number(event?.type) === RRWEB_EVENT_META &&
					Number(event?.timestamp ?? 0) <= fullSnapshotTs
			);
			const bootstrap = [
				...(latestMeta ? [latestMeta] : []),
				...priorEvents.slice(fullSnapshotIndex),
			];
			events = [...bootstrap, ...events];
			truncated = true;
		}
	}

	if (events.length > DOM_REPLAY_MAX_EVENTS) {
		events = events.slice(0, DOM_REPLAY_MAX_EVENTS);
		truncated = true;
	}

	while (events.length > 0) {
		const serializedEvents = JSON.stringify(events);
		if (serializedEvents.length <= DOM_REPLAY_MAX_PAYLOAD_CHARS) {
			break;
		}
		events = events.slice(0, Math.max(1, Math.floor(events.length * 0.85)));
		truncated = true;
	}

	if (events.length === 0) {
		return null;
	}

	return {
		format: "rrweb@2",
		session_id: domReplayState.sessionId,
		started_at: domReplayState.startedAt,
		captured_at: new Date().toISOString(),
		window_ms: DOM_REPLAY_WINDOW_MS,
		event_count: events.length,
		truncated,
		events,
	};
};

const normalizeHeaders = (headers: any): Record<string, any> => {
	if (!headers) return {};
	if (typeof headers.toJSON === "function") {
		return headers.toJSON();
	}

	return { ...headers };
};

const buildFullRequestUrl = (error: any): string => {
	const baseURL = error?.config?.baseURL || "";
	const url = error?.config?.url || "";

	if (!baseURL) return url;
	if (/^https?:\/\//i.test(url)) return url;

	const normalizedBase = String(baseURL).replace(/\/+$/, "");
	const normalizedPath = String(url).replace(/^\/+/, "");
	return `${normalizedBase}/${normalizedPath}`;
};

const buildBrowserData = () => {
	if (typeof window === "undefined") {
		return {};
	}

	const userAgentData = (navigator as any).userAgentData;
	const currentLocation = window.location;
	const queryParams = Object.fromEntries(new URLSearchParams(currentLocation.search).entries());

	return {
		user_agent: navigator.userAgent,
		browser_name: userAgentData?.brands?.[0]?.brand ?? null,
		browser_version: userAgentData?.brands?.[0]?.version ?? null,
		language: navigator.language,
		languages: navigator.languages,
		timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		platform: navigator.platform,
		os: navigator.platform,
		device_memory_gb: (navigator as any).deviceMemory ?? null,
		cpu_cores: navigator.hardwareConcurrency ?? null,
		cookies_enabled: navigator.cookieEnabled,
		origin: currentLocation.origin,
		current_url: currentLocation.href,
		pathname: currentLocation.pathname,
		search: currentLocation.search,
		query_params: queryParams,
		hash: currentLocation.hash,
		route: `${currentLocation.pathname}${currentLocation.search}${currentLocation.hash}`,
		screen: {
			width: window.screen?.width,
			height: window.screen?.height,
			avail_width: window.screen?.availWidth,
			avail_height: window.screen?.availHeight,
			pixel_ratio: window.devicePixelRatio,
		},
		viewport: {
			width: window.innerWidth,
			height: window.innerHeight,
		},
	};
};

const buildUserData = (token: string | null) => {
	if (!token) {
		return {
			username: localStorage.getItem("username"),
			user_id: localStorage.getItem("user_id"),
		};
	}

	const payloadPart = token.split(".")[1];
	if (!payloadPart) {
		return {
			username: localStorage.getItem("username"),
			user_id: localStorage.getItem("user_id"),
		};
	}

	try {
		const decodedPayload = JSON.parse(atob(payloadPart.replace(/-/g, "+").replace(/_/g, "/")));
		return {
			username: decodedPayload?.username ?? null,
			user_id: decodedPayload?.user_id ?? null,
			profile_id: decodedPayload?.profile_id ?? null,
			client: decodedPayload?.client ?? null,
		};
	} catch {
		return {
			username: localStorage.getItem("username"),
			user_id: localStorage.getItem("user_id"),
		};
	}
};

const captureScreenshot = async (): Promise<string | null> => {
	if (typeof window === "undefined" || typeof document === "undefined") {
		return null;
	}

	try {
		const html2canvasModule = await import("html2canvas");
		const html2canvas = ((html2canvasModule as any).default ?? html2canvasModule) as any;
		const canvas = await html2canvas(document.body, {
			backgroundColor: null,
			logging: false,
			useCORS: true,
			scale: 1.0,
		});

		return canvas.toDataURL("image/png");
	} catch (captureError) {
		console.error("[Axios][report-error] screenshot_failed", captureError);
		return null;
	}
};

const reportAxiosError = async (error: any, traceId: string, traceparent: string): Promise<void> => {
	const endpoint = buildReportErrorEndpoint();
	if (!endpoint) {
		enqueueSnackbar("No se pudo enviar el reporte: falta REACT_APP_TK_GW_DOMAIN", { variant: "warning" });
		return;
	}

	const token = getAuthToken();
	const authorization = token ? `Bearer ${token}` : null;
	const screenshotBase64 = await captureScreenshot();
	const domReplay = getDomReplaySnapshot();

	const payload = {
		trace_id: traceId,
		traceparent,
		timestamp: new Date().toISOString(),
		authorization,
		failed_request: {
			method: error?.config?.method,
			url: error?.config?.url,
			base_url: error?.config?.baseURL,
			full_url: buildFullRequestUrl(error),
		},
		request_data: parseErrorData(error?.config?.data),
		request_headers: normalizeHeaders(error?.config?.headers),
		response_received: {
			status: error?.response?.status,
			status_text: error?.response?.statusText,
			data: error?.response?.data,
			headers: normalizeHeaders(error?.response?.headers),
		},
		user: buildUserData(token),
		browser: buildBrowserData(),
		screenshot_base64: screenshotBase64,
		dom_replay: domReplay,
	};

	try {
		await axios.post(endpoint, payload, {
			headers: {
				"Content-Type": "application/json",
				...(authorization ? { Authorization: authorization } : {}),
			},
		});

		enqueueSnackbar("Reporte enviado", {
			variant: "default",
			anchorOrigin: { vertical: "bottom", horizontal: "right" },
			autoHideDuration: 2500,
			style: {
				backgroundColor: "#0f0f0f",
				color: "#9dd39f",
				fontFamily: "Consolas, 'Courier New', monospace",
				fontSize: "12px",
				padding: "8px 10px",
				border: "1px solid #2a2a2a",
			},
		});
	} catch (reportError) {
		console.error("[Axios][report-error] request_failed", reportError);
		enqueueSnackbar("No se pudo enviar el reporte", {
			variant: "warning",
			anchorOrigin: { vertical: "bottom", horizontal: "right" },
			autoHideDuration: 3500,
		});
	}
};

const notifyGlobalAxiosError = (error: any): void => {
	const statusCode = Number(error?.response?.status);
	if (!Number.isFinite(statusCode) || statusCode < 400) {
		return;
	}

	// enqueueSnackbar(errorMessage, { variant: "error" });

	const traceparent = extractRequestTraceparent(error);
	if (traceparent) {
		const traceId = extractTraceIdFromTraceparent(traceparent) ?? traceparent;
		enqueueSnackbar(`Trace: ${traceId}`, {
			variant: "default",
			anchorOrigin: { vertical: "bottom", horizontal: "right" },
			autoHideDuration: 10000,
			action: (snackbarId) =>
				React.createElement(
					"button",
					{
						type: "button",
						onClick: () => {
							closeSnackbar(snackbarId);
							void reportAxiosError(error, traceId, traceparent);
						},
						style: {
							background: "transparent",
							border: "1px solid #3a3a3a",
							color: "#d4d4d4",
							padding: "2px 8px",
							marginLeft: "10px",
							borderRadius: "4px",
							cursor: "pointer",
							fontFamily: "Consolas, 'Courier New', monospace",
							fontSize: "11px",
						},
					},
					"Reportar"
				),
			style: {
				backgroundColor: "#0f0f0f",
				color: "#a3a3a3",
				fontFamily: "Consolas, 'Courier New', monospace",
				fontSize: "12px",
				lineHeight: 1.3,
				padding: "8px 10px",
				minWidth: "280px",
				maxWidth: "360px",
				border: "1px solid #2a2a2a",
			},
		});
		console.error(`[Axios][trace-id]`, traceId);
	}
};

const interceptorErrorResponse = async (error: any) => {
	const originalRequest = error?.config as
		| (AxiosConfigWithMetadata & { _retry?: boolean; _skipAuthRefresh?: boolean })
		| undefined;
	const status = error?.response?.status;
	const hasResponse = !!error?.response;

	if (!originalRequest || originalRequest._skipAuthRefresh) {
		notifyGlobalAxiosError(error);
		return Promise.reject(error);
	}

	const url = `${originalRequest.url || ""}`;
	const isTokenEndpoint = url.includes(TOKEN_ENDPOINT_PATH);

	if (isTokenEndpoint) {
		// El endpoint de refresh falló: la sesión esta muerta.
		notifyGlobalAxiosError(error);
		clearSessionStorage();
		redirectToSignin();
		return Promise.reject(error);
	}

	// Disparamos refresh si:
	//   - el server respondió 401, o
	//   - no hubo response (network/CORS) y tenemos refresh_token guardado.
	// Esto cubre el caso en el que el backend devuelve 401 sin cabeceras CORS y
	// el navegador esconde el status del JS.
	const shouldTryRefresh =
		status === 401 || (!hasResponse && !!getRefreshToken());
	if (!shouldTryRefresh) {
		notifyGlobalAxiosError(error);
		return Promise.reject(error);
	}

	if (originalRequest._retry) {
		notifyGlobalAxiosError(error);
		clearSessionStorage();
		redirectToSignin();
		return Promise.reject(error);
	}
	originalRequest._retry = true;

	if (isRefreshing) {
		return new Promise((resolve, reject) => {
			subscribeTokenRefresh((newToken) => {
				if (!newToken) {
					notifyGlobalAxiosError(error);
					reject(error);
					return;
				}
				originalRequest.headers = originalRequest.headers || ({} as any);
				(originalRequest.headers as any).Authorization = `Bearer ${newToken}`;
				resolve(axios(originalRequest));
			});
		});
	}

	isRefreshing = true;
	const newToken = await refreshAccessToken();
	isRefreshing = false;
	onRefreshed(newToken);

	if (!newToken) {
		notifyGlobalAxiosError(error);
		clearSessionStorage();
		redirectToSignin();
		return Promise.reject(error);
	}

	originalRequest.headers = originalRequest.headers || ({} as any);
	(originalRequest.headers as any).Authorization = `Bearer ${newToken}`;
	return axios(originalRequest);
};

const axiosClient = axios.create({
	timeout: 1000 * 60,
});
axiosClient.interceptors.request.use(interceptRequest);
axiosClient.interceptors.response.use(
	interceptSuccessResponse,
	interceptorErrorResponse
);

export const createApiClient = (baseURL: string): AxiosInstance => {
	const token = localStorage.getItem('access_token');
	const instance = axios.create({
		baseURL: baseURL,
		timeout: 1000 * 60,
		headers: token
			? {
				Authorization: `Bearer ${token}`,
				'Content-Type': 'application/json',
			}
			: {
				'Content-Type': 'application/json',
			}
	});

	instance.interceptors.request.use(interceptRequest);
	instance.interceptors.response.use(
		interceptSuccessResponse,
		interceptorErrorResponse
	);
	return instance
};

function getCurrentUrl(): any {
	return window.location.href;
}

function getCurrentRoute() {
	return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

startDomReplayCaptureIfNeeded();
